import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllStopSales,
  deleteStopSale,
  addPackagePriceStopSales,
  updateStopSale,
  pricingMaster,
} from "../../../services/packageService";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import { AdminPageLayout, AdminPageHeader, AdminPageCard } from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { formatListingDate } from "../../../utils/formatListingDate";
import { paginateClientList } from "../../../utils/clientPagination";

const CalendarIcon = () => (
  <svg
    className="w-4 h-4 text-gray-500"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const WEEKDAYS = [
  { day: "Sun", full: "Sunday", key: "sun" },
  { day: "Mon", full: "Monday", key: "mon" },
  { day: "Tue", full: "Tuesday", key: "tue" },
  { day: "Wed", full: "Wednesday", key: "wed" },
  { day: "Thu", full: "Thursday", key: "thu" },
  { day: "Fri", full: "Friday", key: "fri" },
  { day: "Sat", full: "Saturday", key: "sat" },
];

const StopSales = () => {
  const navigate = useNavigate();
  const [showCalendarSheet, setShowCalendarSheet] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [tourTypeFilter, setTourTypeFilter] = useState("all"); // all, fixed_departure, private
  const [showToster, setShowToster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingStopSale, setEditingStopSale] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedStayCategory, setSelectedStayCategory] = useState(null);
  const [packageStayCategories, setPackageStayCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToasterMessage(message);
    setToasterType(type);
    setShowToster(true);
  };

  // Fetch stop sales
  const {
    data: stopSalesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["stop-sales-all"],
    queryFn: () => getAllStopSales(),
  });

  // Fetch package stay categories when package is selected
  const { data: stayCategoriesData } = useQuery({
    queryKey: ["package-stay-categories", selectedPackage?.id],
    queryFn: () => pricingMaster(selectedPackage?.id),
    enabled: !!selectedPackage?.id,
  });

  useEffect(() => {
    if (stayCategoriesData?.data) {
      setPackageStayCategories(stayCategoriesData.data);
    }
  }, [stayCategoriesData]);

  // Delete stop sale mutation
  const deleteStopSaleMutation = useMutation({
    mutationFn: (id) => deleteStopSale(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["stop-sales-all"]);
      showToast("Stop sale deleted successfully", "success");
    },
    onError: (error) => {
      showToast(
        error?.response?.data?.message || "Error deleting stop sale",
        "error"
      );
    },
  });

  // Add stop sale mutation
  const addStopSaleMutation = useMutation({
    mutationFn: (data) => addPackagePriceStopSales(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["stop-sales-all"]);
      showToast("Stop sale added successfully", "success");
      setShowAddDialog(false);
      resetForm();
    },
    onError: (error) => {
      showToast(
        error?.response?.data?.message || "Error adding stop sale",
        "error"
      );
    },
  });

  // Update stop sale mutation
  const updateStopSaleMutation = useMutation({
    mutationFn: ({ stopSaleId, data }) => updateStopSale(stopSaleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["stop-sales-all"]);
      showToast("Stop sale updated successfully", "success");
      setShowAddDialog(false);
      resetForm();
    },
    onError: (error) => {
      showToast(
        error?.response?.data?.message || "Error updating stop sale",
        "error"
      );
    },
  });

  const resetForm = () => {
    setEditingStopSale(null);
    setSelectedDays([]);
    setDateRange({ start: "", end: "" });
    setSelectedPackage(null);
    setSelectedStayCategory(null);
  };

  const handleDelete = (stopSaleId) => {
    if (window.confirm("Are you sure you want to delete this stop sale?")) {
      deleteStopSaleMutation.mutate(stopSaleId);
    }
  };

  const handleAddNew = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const handleEdit = (stopSale, packageData) => {
    setEditingStopSale(stopSale);
    setSelectedPackage({ id: packageData.id, name: packageData.name });
    setDateRange({
      start: stopSale.start_date,
      end: stopSale.end_date,
    });

    // Set selected days from applicable_days
    const days = [];
    if (stopSale.applicable_days) {
      WEEKDAYS.forEach((wd) => {
        if (stopSale.applicable_days[wd.key]) {
          days.push(wd.key);
        }
      });
    }
    setSelectedDays(days);
    setSelectedStayCategory(stopSale.stay_category_id);
    setShowAddDialog(true);
  };

  const toggleDay = (dayKey) => {
    setSelectedDays((prev) =>
      prev.includes(dayKey)
        ? prev.filter((d) => d !== dayKey)
        : [...prev, dayKey]
    );
  };

  const handleSaveStopSale = () => {
    if (!selectedPackage) {
      showToast("Please select a package", "error");
      return;
    }

    if (!dateRange.start || !dateRange.end) {
      showToast("Please select date range", "error");
      return;
    }

    if (selectedDays.length === 0) {
      showToast("Please select at least one day", "error");
      return;
    }

    if (!selectedStayCategory) {
      showToast("Please select a stay category", "error");
      return;
    }

    // Find package stay category ID
    const packageStayCategory = packageStayCategories.find(
      (psc) => psc.stay_category_id === selectedStayCategory
    );

    if (!packageStayCategory) {
      showToast("Stay category not found for this package", "error");
      return;
    }

    const applicableDays = {};
    WEEKDAYS.forEach((wd) => {
      applicableDays[wd.key] = selectedDays.includes(wd.key) ? 1 : 0;
    });

    const data = {
      package_id: selectedPackage.id,
      package_stay_category_id: packageStayCategory.id,
      stay_category_id: selectedStayCategory,
      start_date: dateRange.start,
      end_date: dateRange.end,
      stop_sale_applicable_days: JSON.stringify(applicableDays),
    };

    // If editing, use update mutation; otherwise use add mutation
    if (editingStopSale) {
      updateStopSaleMutation.mutate({
        stopSaleId: editingStopSale.id,
        data: data,
      });
    } else {
      addStopSaleMutation.mutate(data);
    }
  };

  // Filter and search packages
  const getFilteredData = () => {
    if (!stopSalesData?.data) return [];

    // Handle both old format (array) and new format (object with stopSales and allPackages)
    const stopSales = Array.isArray(stopSalesData.data)
      ? stopSalesData.data
      : stopSalesData.data.stopSales || [];

    let result = stopSales;

    // Apply tour type filter
    if (tourTypeFilter !== "all") {
      result = result.map((trip) => ({
        ...trip,
        packages: trip.packages.filter((pkg) => {
          if (tourTypeFilter === "fixed_departure") {
            return pkg.tour_type === "fixed_departure";
          } else if (tourTypeFilter === "private") {
            return pkg.tour_type === "private" || pkg.tour_type === "private_tour";
          }
          return true;
        }),
      }));
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.map((trip) => ({
        ...trip,
        packages: trip.packages.filter(
          (pkg) =>
            pkg.name.toLowerCase().includes(query) ||
            trip.tripName.toLowerCase().includes(query)
        ),
      }));
    }

    return result.filter((trip) => trip.packages.length > 0);
  };

  const filteredData = getFilteredData();

  const stopSaleRows = useMemo(() => {
    const rows = [];
    filteredData.forEach((trip) => {
      trip.packages.forEach((pkg) => {
        pkg.stopSales.forEach((stopSale) => {
          rows.push({ trip, pkg, stopSale });
        });
      });
    });
    return rows;
  }, [filteredData]);

  const { data: pagedStopSaleRows, pagination } = useMemo(
    () => paginateClientList(stopSaleRows, currentPage),
    [stopSaleRows, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, tourTypeFilter, stopSalesData]);

  // Get calendar dates with stop sales (includes both fixed_departure and private packages)
  const getCalendarDates = () => {
    const dates = {};
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();

    if (!stopSalesData?.data) return dates;

    // Handle both old format (array) and new format (object with stopSales and allPackages)
    const stopSales = Array.isArray(stopSalesData.data)
      ? stopSalesData.data
      : stopSalesData.data.stopSales || [];

    stopSales.forEach((trip) => {
      trip.packages.forEach((pkg) => {
        // Include both fixed_departure and private tour packages
        if (pkg.tour_type === "fixed_departure" || pkg.tour_type === "private" || pkg.tour_type === "private_tour") {
          pkg.stopSales.forEach((stopSale) => {
            const start = new Date(stopSale.start_date);
            const end = new Date(stopSale.end_date);

            // Check if stop sale is applicable for each day in the range
            for (
              let d = new Date(start);
              d <= end;
              d.setDate(d.getDate() + 1)
            ) {
              const dateKey = d.toISOString().split("T")[0];
              const dayOfWeek = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
              const dayKey = WEEKDAYS[dayOfWeek].key; // sun, mon, tue, etc.

              // Check if this day is blocked by the stop sale
              if (stopSale.applicable_days && stopSale.applicable_days[dayKey] === 1) {
                if (
                  d.getFullYear() === year &&
                  d.getMonth() === month
                ) {
                  if (!dates[dateKey]) {
                    dates[dateKey] = [];
                  }
                  dates[dateKey].push({
                    package: pkg.name,
                    trip: trip.tripName,
                    tourType: pkg.tour_type === "fixed_departure" ? "Fixed" : "Private",
                    days: stopSale.applicable_days,
                  });
                }
              }
            }
          });
        }
      });
    });

    return dates;
  };

  const calendarDates = getCalendarDates();

  const formatDate = formatListingDate;

  // Get day name from date
  const getDayName = (dateString) => {
    const date = new Date(dateString);
    const dayIndex = date.getDay();
    return WEEKDAYS[dayIndex].full;
  };

  // Check if date is in stop sale range and applicable for that day
  const isDateBlocked = (dateString, stopSale) => {
    const date = new Date(dateString);
    const dayIndex = date.getDay();
    const dayKey = WEEKDAYS[dayIndex].key;

    if (
      date >= new Date(stopSale.start_date) &&
      date <= new Date(stopSale.end_date)
    ) {
      return stopSale.applicable_days?.[dayKey] === 1;
    }
    return false;
  };

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader
          title={SUPPLIER_PAGE_META.stopSales.title}
          subtitle={SUPPLIER_PAGE_META.stopSales.subtitle}
        />
        <AdminPageCard className="flex h-96 items-center justify-center p-6">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
            <p className="text-sm text-gray-600">
              Please wait while we fetch your stop sales data
            </p>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader
          title={SUPPLIER_PAGE_META.stopSales.title}
          subtitle={SUPPLIER_PAGE_META.stopSales.subtitle}
        />
        <AdminPageCard className="flex h-96 flex-col items-center justify-center p-6 text-center">
          <i className="fi fi-rr-exclamation-triangle mb-4 text-4xl text-red-500" />
          <p className="mb-4 text-gray-600">
            Error loading stop sales: {error?.message || "Unknown error"}
          </p>
          <button
            onClick={() => refetch()}
            className="rounded bg-primary px-4 py-2 text-white hover:bg-primary-dark"
          >
            Try Again
          </button>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      {showToster && toasterMessage && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          onClose={() => {
            setShowToster(false);
            setToasterMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={SUPPLIER_PAGE_META.stopSales.title}
        subtitle={SUPPLIER_PAGE_META.stopSales.subtitle}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/packages")}
              className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:bg-gray-50"
            >
              <i className="fi fi-rr-angle-left text-xs" />
              Packages
            </button>
            <button
              onClick={() => setShowCalendarSheet(true)}
              className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-all hover:bg-blue-100"
            >
              <i className="fi fi-rr-calendar text-xs" />
              <span>View Calendar</span>
            </button>
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 rounded-md bg-primary-600 px-4 py-1.5 text-xs font-medium uppercase text-white shadow-sm transition-all hover:bg-primary-700"
            >
              <i className="fi fi-rr-plus-small text-base" />
              <span>Add Stop Sale</span>
            </button>
          </div>
        }
      />

      <AdminPageCard className="p-4 lg:p-6">
        <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* Search & Filter Control */}
          <div className="lg:col-span-8 bg-white p-3 rounded-lg border border-gray-200 shadow-sm flex flex-col lg:flex-row items-center gap-3">
            <div className="flex-1 relative w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search packages, destinations..."
                className="w-full h-9 px-9 bg-gray-50 border border-gray-200 rounded-md text-sm font-medium focus:bg-white focus:ring-1 focus:ring-primary focus:border-primary transition-all"
              />
              <i className="fi fi-rr-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <select
                value={tourTypeFilter}
                onChange={(e) => setTourTypeFilter(e.target.value)}
                className="h-9 px-3 bg-gray-50 border border-gray-200 rounded-md text-xs font-medium focus:bg-white focus:ring-1 focus:ring-primary focus:border-primary transition-all outline-none"
              >
                <option value="all">All Tour Types</option>
                <option value="fixed_departure">Fixed Departure</option>
                <option value="private">Private Tour</option>
              </select>
              {(searchQuery || tourTypeFilter !== "all") && (
                <button
                  onClick={() => { setSearchQuery(""); setTourTypeFilter("all"); }}
                  className="h-9 px-3 bg-red-50 text-red-600 border border-red-100 rounded-md text-xs font-medium hover:bg-red-100 transition-all"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Statistics Mini Row */}
          <div className="lg:col-span-4 bg-white p-3 rounded-lg border border-gray-200 shadow-sm flex items-center justify-around">
            <div className="text-center">
              <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Active Blocks</p>
              <p className="text-xl font-bold text-gray-900">
                {filteredData.reduce((acc, trip) => acc + trip.packages.reduce((pAcc, pkg) => pAcc + pkg.stopSales.length, 0), 0)}
              </p>
            </div>
            <div className="w-px h-8 bg-gray-100"></div>
            <div className="text-center">
              <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Destinations</p>
              <p className="text-xl font-bold text-primary-600">{filteredData.length}</p>
            </div>
          </div>
        </div>

        {/* Content Section: Striped Package Groups */}
        <div className="space-y-6">
          {filteredData.length === 0 ? (
            <div className="bg-white rounded-lg p-12 text-center border border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fi fi-rr-calendar-minus text-2xl text-gray-300"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">No Blocked Dates Found</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                {searchQuery || tourTypeFilter !== "all"
                  ? "We couldn't find any stop sales matching your active filters. Try resetting the search."
                  : "Every package is currently open for booking. Block dates to stop sales manually."}
              </p>
              <button
                onClick={handleAddNew}
                className="mt-6 px-6 py-2 bg-primary-600 text-white font-medium text-xs rounded-md shadow-sm transition-all active:scale-95 uppercase"
              >
                Create New Stop Sale
              </button>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 gap-3">
              {pagedStopSaleRows.map(({ trip, pkg, stopSale }) => (
                      <div
                        key={stopSale.id}
                        className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all group overflow-hidden"
                      >
                        <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-6">

                          {/* Left: Package Info */}
                          <div className="flex items-center gap-3 min-w-0 lg:w-[32%] shrink-0">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${pkg.tour_type === 'fixed_departure' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-purple-50 text-purple-600 border-purple-100'
                              }`}>
                              <i className={`fi ${pkg.tour_type === 'fixed_departure' ? 'fi-rr-user-gear' : 'fi-rr-users-alt'} text-sm`}></i>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] font-bold text-gray-400 uppercase mb-0.5">{trip.tripName}</p>
                              <h3 className="font-semibold text-gray-900 text-sm truncate group-hover:text-primary-600 transition-colors uppercase">{pkg.name}</h3>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${pkg.tour_type === 'fixed_departure' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-purple-50 text-purple-700 border-purple-100'
                                  }`}>
                                  {pkg.tour_type === 'fixed_departure' ? 'Fixed Tour' : 'Private'}
                                </span>
                                <span className="text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100 truncate">{stopSale.stay_category_name || "All Stays"}</span>
                              </div>
                            </div>
                          </div>

                          {/* Middle: Blocked Date range & Days */}
                          <div className="flex items-center gap-6 flex-1">
                            <div className="flex flex-col">
                              <span className="text-[9px] font-bold text-gray-400 uppercase mb-1.5 leading-none">Blocked Duration</span>
                              <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                                <span className="bg-gray-50 px-2.5 py-1 rounded border border-gray-100">{formatDate(stopSale.start_date)}</span>
                                <i className="fi fi-rr-arrow-small-right text-gray-300"></i>
                                <span className="bg-gray-50 px-2.5 py-1 rounded border border-gray-100">{formatDate(stopSale.end_date)}</span>
                              </div>
                            </div>

                            <div className="flex flex-col flex-1">
                              <span className="text-[9px] font-bold text-gray-400 uppercase mb-1.5 leading-none">Applicable Days</span>
                              <div className="flex flex-wrap gap-1">
                                {WEEKDAYS.map((wd) => (
                                  <div
                                    key={wd.key}
                                    className={`text-[9px] font-bold w-6 h-6 flex items-center justify-center rounded border uppercase transition-all ${stopSale.applicable_days?.[wd.key]
                                      ? 'bg-red-500 border-red-500 text-white shadow-sm'
                                      : 'bg-gray-50 border-gray-100 text-gray-300'
                                      }`}
                                  >
                                    {wd.day.charAt(0)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Right: Modern Actions */}
                          <div className="flex items-center gap-2 shrink-0 justify-end">
                            <button
                              onClick={() => handleEdit(stopSale, pkg)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-50 text-gray-500 rounded-md hover:bg-white hover:border-gray-300 transition-all border border-gray-200"
                              title="Edit Logic"
                            >
                              <i className="fi fi-rr-edit-alt text-[11px]"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(stopSale.id)}
                              className="w-8 h-8 flex items-center justify-center bg-gray-50 text-gray-500 rounded-md hover:bg-white hover:border-gray-300 transition-all border border-gray-200"
                              title="Revoke Stop Sale"
                            >
                              <i className="fi fi-rr-trash text-[11px]"></i>
                            </button>
                            <div className="w-px h-5 bg-gray-200 mx-1"></div>
                            <button
                              onClick={() =>
                                navigate(`/packages/${pkg.id}/edit`, {
                                  state: {
                                    initialTab: "pricing",
                                    returnTo: "/stop-sales",
                                  },
                                })
                              }
                              className="h-8 px-3 text-primary hover:text-white bg-gray-50 hover:bg-primary border border-gray-200 hover:border-primary rounded-md text-[10px] font-medium uppercase transition-all active:scale-95"
                            >
                              Pricing Logic
                            </button>
                          </div>
                        </div>
                      </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 bg-white rounded-lg">
              <Pagination pagination={pagination} onPageChange={setCurrentPage} />
            </div>
            </>
          )}
        </div>
        </div>
      </AdminPageCard>

      {/* Add/Edit Dialog - Redesigned */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-gray-950/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 font-sans animate-in fade-in duration-200">
          <div className="bg-white rounded-lg max-w-2xl w-full border border-gray-200 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 border border-primary-100">
                  <i className="fi fi-rr-ban text-lg"></i>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {editingStopSale ? "Modify Stop Sale" : "New Stop Sale Rule"}
                  </h2>
                  <p className="text-xs font-medium text-gray-500 uppercase">Restrict Inventory Access</p>
                </div>
              </div>
              <button
                onClick={() => { setShowAddDialog(false); resetForm(); }}
                className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-colors"
              >
                <i className="fi fi-rr-cross-small text-lg font-bold"></i>
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6 bg-white">
              {/* Form Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase block mb-3">Target Trip Package</label>
                  <div className="relative">
                    <select
                      value={selectedPackage?.id || ""}
                      onChange={async (e) => {
                        const pkgId = parseInt(e.target.value);
                        const allPackages = stopSalesData?.data?.allPackages || [];
                        const pkg = allPackages.find((p) => p.id === pkgId);
                        if (pkg) setSelectedPackage({ id: pkg.id, name: pkg.name });
                        setSelectedStayCategory(null);
                        setPackageStayCategories([]);
                      }}
                      className="w-full h-12 pl-12 pr-4 bg-gray-50 border border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all appearance-none"
                      disabled={!!editingStopSale}
                    >
                      <option value="">Select Package...</option>
                      {(stopSalesData?.data?.allPackages || []).map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.name} — {pkg.tour_type === "fixed_departure" ? "Fixed" : "Private"}
                        </option>
                      ))}
                    </select>
                    <i className="fi fi-rr-box absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase block mb-3">Stay Category Coverage</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {packageStayCategories.map((psc) => (
                      <div
                        key={psc.id}
                        onClick={() => setSelectedStayCategory(psc.stay_category_id)}
                        className={`cursor-pointer p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${selectedStayCategory === psc.stay_category_id
                          ? 'bg-primary-50 border-primary-600 shadow-sm'
                          : 'bg-white border-gray-100 hover:border-gray-200'
                          }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedStayCategory === psc.stay_category_id ? 'bg-primary-600 text-white' : 'bg-gray-50 text-gray-400'
                          }`}>
                          <i className="fi fi-rr-bed text-xs"></i>
                        </div>
                        <span className={`text-xs font-black uppercase tracking-tight ${selectedStayCategory === psc.stay_category_id ? 'text-primary-900' : 'text-gray-500'
                          }`}>
                          {psc.stayCategory?.title || "Stay Class"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] font-black text-gray-400 uppercase block mb-3">Begin Date</label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="h-12 px-4 bg-gray-50 border border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all outline-none"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] font-black text-gray-400 uppercase block mb-3">Final Date</label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    min={dateRange.start}
                    className="h-12 px-4 bg-gray-50 border border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all outline-none"
                  />
                </div>

                <div className="md:col-span-2 mt-2">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-3">Week Selection</label>
                  <div className="grid grid-cols-7 gap-2">
                    {WEEKDAYS.map((wd) => (
                      <button
                        key={wd.key}
                        onClick={() => toggleDay(wd.key)}
                        className={`h-10 flex flex-col items-center justify-center rounded-lg border transition-all ${selectedDays.includes(wd.key)
                          ? "bg-red-500 border-red-500 text-white shadow-md"
                          : "bg-white border-gray-200 text-gray-400 hover:border-gray-300"
                          }`}
                      >
                        <span className="text-[10px] font-bold">{wd.day.toUpperCase()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowAddDialog(false); resetForm(); }}
                className="h-10 px-5 bg-white border border-gray-200 text-gray-500 font-semibold text-xs rounded-md hover:bg-gray-100 transition-colors uppercase"
              >
                Discard
              </button>
              <button
                onClick={handleSaveStopSale}
                disabled={addStopSaleMutation.isPending || updateStopSaleMutation.isPending}
                className="h-10 px-6 bg-primary-600 text-white font-semibold text-xs rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-all flex items-center gap-2 uppercase"
              >
                {(addStopSaleMutation.isPending || updateStopSaleMutation.isPending) && <div className="w-3.5 h-3.5 rounded-full border-2 border-white/20 border-t-white animate-spin"></div>}
                {editingStopSale ? "Apply Update" : "Deploy Logic"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sheet - Live Monitor Calendar */}
      {showCalendarSheet && (
        <>
          <div className="fixed inset-0 bg-gray-950/40 backdrop-blur-[2px] z-[110] animate-in fade-in duration-300" onClick={() => setShowCalendarSheet(false)}></div>
          <div className="fixed top-0 right-0 bottom-0 bg-white w-full max-w-md border-l border-gray-100 shadow-2xl z-[120] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-white text-gray-900">
              <div>
                <h3 className="text-lg font-bold tracking-tight">Activity Monitor</h3>
                <p className="text-[10px] font-semibold text-gray-500 uppercase">Blocked Availability Feed</p>
              </div>
              <button onClick={() => setShowCalendarSheet(false)} className="w-9 h-9 flex items-center justify-center rounded-md bg-gray-50 hover:bg-gray-100 text-gray-400">
                <i className="fi fi-rr-cross-small text-xl font-bold"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Internal Calendar Syncing Header */}
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-base font-bold text-gray-900">
                  {calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </h4>
                <div className="flex items-center gap-2">
                  <button onClick={() => { const pm = new Date(calendarMonth); pm.setMonth(pm.getMonth() - 1); setCalendarMonth(pm); }} className="w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center hover:bg-gray-100 transition-colors border border-gray-100"><i className="fi fi-rr-angle-left text-xs"></i></button>
                  <button onClick={() => { const nm = new Date(calendarMonth); nm.setMonth(nm.getMonth() + 1); setCalendarMonth(nm); }} className="w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center hover:bg-gray-100 transition-colors border border-gray-100"><i className="fi fi-rr-angle-right text-xs"></i></button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-2">
                {WEEKDAYS.map(wd => <div key={wd.key} className="text-center text-[10px] font-black text-gray-400 uppercase pb-2">{wd.day}</div>)}
                {Array.from({ length: 35 }).map((_, idx) => {
                  const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), idx - new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).getDay() + 1);
                  const dateKey = date.toISOString().split("T")[0];
                  const isFocus = date.getMonth() === calendarMonth.getMonth();
                  const blockedInfo = calendarDates[dateKey] || [];

                  return (
                    <div key={idx} className={`aspect-square p-1.5 rounded-xl border flex flex-col items-center justify-between transition-all ${isFocus
                      ? (blockedInfo.length > 0 ? 'bg-red-50/50 border-red-200' : 'bg-gray-50 border-gray-100 hover:border-gray-200')
                      : 'opacity-20 pointer-events-none'
                      }`}>
                      <span className={`text-[10px] font-black ${blockedInfo.length > 0 ? 'text-red-700' : 'text-gray-400'}`}>{date.getDate()}</span>
                      {blockedInfo.length > 0 && <div className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></div>}
                    </div>
                  );
                })}
              </div>

              {/* Day details feed */}
              <div className="pt-4 space-y-4">
                <span className="text-[10px] font-black text-gray-400 uppercase border-b border-gray-100 block pb-2">Blocked Units Feed</span>
                {Object.entries(calendarDates).filter(([date]) => new Date(date).getMonth() === calendarMonth.getMonth()).map(([date, infos]) => (
                  <div key={date} className="space-y-2">
                    <p className="text-[10px] font-black text-gray-900 border-l-2 border-red-600 pl-2">{formatDate(date)}</p>
                    <div className="space-y-1 ml-2">
                      {infos.map((info, i) => (
                        <div key={i} className="bg-red-50 p-2 rounded-lg border border-red-100 flex items-center justify-between">
                          <span className="text-[10px] font-bold text-red-800 truncate max-w-[150px]">{info.package}</span>
                          <span className="text-[8px] font-black uppercase text-red-600">{info.tourType}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

    </AdminPageLayout>
  );
};

export default StopSales;

